import { environment } from 'src/environments/environment';

export const BETA_HANDOFF_READY = 'alertops-beta-handoff-ready';
export const BETA_HANDOFF_SESSION = 'alertops-beta-handoff-session';
export const BETA_HANDOFF_PARAM = 'betaHandoff';
export const CLASSIC_HANDOFF_PARAM = 'classicHandoff';
export const BETA_HANDOFF_REFRESH_AUTHME_KEY = 'alertops-beta-handoff-refresh-authme';

/** Keys copied from classic UI → new UI. Keep in sync with alertops beta-session-handoff.ts */
export const BETA_HANDOFF_STATIC_ALLOWLIST = [
  'token',
  'refresh_token',
  'token-key',
  'username',
  'userLocalStorageToken',
  'subdomain',
  'email',
  'identityprovider',
  'currentTheme',
] as const;

/** Never copy — navigation/logout side effects or stale deep-link state */
export const BETA_HANDOFF_DENYLIST = [
  'logout-event',
  'login-event',
  'addUrlToCache',
  'addLoginFlag',
  'loginAttemptCount',
] as const;

const denySet = new Set<string>(BETA_HANDOFF_DENYLIST);
const staticAllowSet = new Set<string>(BETA_HANDOFF_STATIC_ALLOWLIST);

export function isBetaHandoffStorageKeyAllowed(key: string): boolean {
  if (!key || denySet.has(key)) {
    return false;
  }
  if (staticAllowSet.has(key)) {
    return true;
  }
  // Versioned auth blob from classic (`{appVersion}-key`), when it differs from token-key.
  return key.endsWith('-key');
}

export function collectAllowedLocalStorage(source: Storage = localStorage): Record<string, string> {
  const snapshot: Record<string, string> = {};
  for (let i = 0; i < source.length; i++) {
    const key = source.key(i);
    if (!key || !isBetaHandoffStorageKeyAllowed(key)) {
      continue;
    }
    const value = source.getItem(key);
    if (value != null) {
      snapshot[key] = value;
    }
  }
  return snapshot;
}

export function applyHandoffStorage(
  storage: Record<string, string> | null | undefined,
  target: Storage = localStorage,
): void {
  if (!storage) {
    return;
  }
  writeAllowedHandoffEntries(storage, target);
  ensureVersionedAuthBlob(target);
}

/** Allowlisted key copy only. Does not create the versioned auth blob. */
export function writeAllowedHandoffEntries(
  storage: Record<string, string> | null | undefined,
  target: Storage = localStorage,
): void {
  if (!storage) {
    return;
  }
  for (const [key, value] of Object.entries(storage)) {
    if (!isBetaHandoffStorageKeyAllowed(key) || typeof value !== 'string') {
      continue;
    }
    target.setItem(key, value);
  }
}

/** After token copy, ensure new UI auth blob exists for AuthService.getAuthFromLocalStorage(). */
export function ensureVersionedAuthBlob(target: Storage = localStorage): void {
  const authKey = `${environment.appVersion}-${environment.USERDATA_KEY}`;
  if (target.getItem(authKey) || !target.getItem('token')) {
    return;
  }

  const refreshToken = target.getItem('refresh_token') ?? '';
  let account = target.getItem('subdomain') ?? '';
  try {
    const tokenKey = JSON.parse(target.getItem('token-key') || 'null');
    account = String(tokenKey?.account ?? account).trim().toLowerCase();
  } catch {
    // keep subdomain
  }

  const blob = {
    token: target.getItem('token'),
    refresh_token: refreshToken,
    account,
  };
  target.setItem(authKey, JSON.stringify(blob));
}

export function markBetaHandoffAuthMeRefreshPending(): void {
  sessionStorage.setItem(BETA_HANDOFF_REFRESH_AUTHME_KEY, '1');
}

export function consumeBetaHandoffAuthMeRefreshPending(): boolean {
  const pending = sessionStorage.getItem(BETA_HANDOFF_REFRESH_AUTHME_KEY) === '1';
  if (pending) {
    sessionStorage.removeItem(BETA_HANDOFF_REFRESH_AUTHME_KEY);
  }
  return pending;
}

export function resolveClassicOpenerOrigin(): string {
  try {
    const referrer = document.referrer;
    if (!referrer) {
      return '*';
    }
    return new URL(referrer).origin;
  } catch {
    return '*';
  }
}
